import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App } from './App';
import { ApiProvider } from './api/ApiProvider';
import { createHttpClient } from './api/client';
import { createDemoClient } from './api/demo';
import { AuthProvider } from './auth/AuthProvider';
import { createCognitoAuth } from './auth/cognito';
import { createDemoAuth } from './auth/demo';
import { ToastProvider } from './components/Toast';
import { DEMO_MODE, loadConfig } from './config';
import { applyTheme, readStoredPreference, watchSystemTheme } from './theme';
import './styles/global.css';

// Keep <html data-theme> and the mobile browser chrome in step with the
// stored preference, including when the device flips while the app is open.
applyTheme(readStoredPreference());
watchSystemTheme(() => applyTheme(readStoredPreference()));

const root = createRoot(document.getElementById('root')!);

async function bootstrap() {
  const config = await loadConfig();
  const auth = DEMO_MODE ? createDemoAuth() : createCognitoAuth(config);
  const api = DEMO_MODE ? createDemoClient() : createHttpClient(config.apiUrl, () => auth.getIdToken());
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: { retry: (count, err) => !(err instanceof Error && /^(401|403|404)/.test(String((err as { status?: number }).status ?? ''))) && count < 2, refetchOnWindowFocus: true },
    },
  });
  document.title = `${config.mspName} · IoT dashboard`;
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <AuthProvider backend={auth}>
          <ApiProvider client={api}>
            <ToastProvider>
              <App config={config} />
            </ToastProvider>
          </ApiProvider>
        </AuthProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
}

bootstrap().catch((err: unknown) => {
  root.render(
    <div style={{ fontFamily: 'system-ui, sans-serif', padding: 32, maxWidth: 640 }}>
      <h1>Dashboard could not start</h1>
      <p>{err instanceof Error ? err.message : String(err)}</p>
    </div>,
  );
});
